import {
  Chart as ChartJS,
  BarController,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js'
import annotationPlugin from 'chartjs-plugin-annotation'

let registered = false

/**
 * Read a CSS custom property off `:root`. Falls back to the supplied default
 * when the variable is undefined (e.g. in jsdom test environments without the
 * stylesheet attached).
 */
function readToken(name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim()
  return value !== '' ? value : fallback
}

/**
 * Returns `true` when the user has expressed a preference for reduced motion.
 * Wrapped so primitives can decide animation duration without importing the
 * media-query check twice.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export interface PaperTokens {
  ink: string
  ink2: string
  ink3: string
  ink4: string
  line: string
  lineSubtle: string
  paper: string
  paperRaised: string
  paperSunken: string
  terracotta: string
  terracottaBg: string
  terracottaLine: string
  sage: string
  sageBg: string
  sageBg2: string
  sageLine: string
  ochre: string
  ochreBg: string
  ochreLine: string
  clay: string
  clayBg: string
  clayBg2: string
  clayLine: string
  indigo: string
  indigoFg: string
  indigoLine: string
  fontSans: string
  fontMono: string
}

/**
 * Snapshot the warm-paper design tokens off the live stylesheet. Reading at
 * call-time (instead of module import) means future dark-mode toggles flow
 * through without rebuilding the Chart.js defaults cache.
 */
export function readPaperTokens(): PaperTokens {
  return {
    ink: readToken('--ink', '#1F1B14'),
    ink2: readToken('--ink-2', '#6B6359'),
    ink3: readToken('--ink-3', '#A39989'),
    ink4: readToken('--ink-4', '#C9C0AE'),
    line: readToken('--line', '#E8E2D5'),
    lineSubtle: readToken('--line-subtle', '#EFE9DD'),
    paper: readToken('--paper', '#FAF7F0'),
    paperRaised: readToken('--paper-raised', '#FFFCF6'),
    paperSunken: readToken('--paper-sunken', '#EDE8DC'),
    terracotta: readToken('--primary', '#C66B3D'),
    terracottaBg: readToken('--primary-bg', 'rgba(198, 107, 61, 0.14)'),
    terracottaLine: readToken('--primary-line', 'rgba(198, 107, 61, 0.32)'),
    sage: readToken('--success', '#2D7A6B'),
    sageBg: readToken('--success-bg', 'rgba(45, 122, 107, 0.14)'),
    sageBg2: readToken('--success-bg-2', 'rgba(45, 122, 107, 0.22)'),
    sageLine: readToken('--success-line', 'rgba(45, 122, 107, 0.32)'),
    ochre: readToken('--warning', '#D4A547'),
    ochreBg: readToken('--warning-bg', 'rgba(212, 165, 71, 0.22)'),
    ochreLine: readToken('--warning-line', 'rgba(212, 165, 71, 0.44)'),
    clay: readToken('--danger', '#8A2E1C'),
    clayBg: readToken('--danger-bg', 'rgba(138, 46, 28, 0.12)'),
    clayBg2: readToken('--danger-bg-2', 'rgba(138, 46, 28, 0.22)'),
    clayLine: readToken('--danger-line', 'rgba(138, 46, 28, 0.28)'),
    indigo: readToken('--info', '#3D4A6B'),
    indigoFg: readToken('--info-fg', '#2C3650'),
    indigoLine: readToken('--info-line', 'rgba(61, 74, 107, 0.22)'),
    fontSans: readToken('--font-sans', 'Inter, system-ui, sans-serif'),
    fontMono: readToken('--font-mono', 'JetBrains Mono, ui-monospace, monospace'),
  }
}

/**
 * Register only the Chart.js modules used by LMS primitives and apply the
 * warm-paper defaults to `Chart.defaults`. Safe to call multiple times —
 * Chart.register itself is idempotent, and we gate the defaults assignment
 * behind a flag so re-renders don't keep re-reading tokens.
 *
 * Call this once near app entry (`main.tsx`) and from each primitive's module
 * scope as a belt-and-braces guard — primitives may be lazily imported via
 * Storybook before `main.tsx` runs.
 */
export function registerChartDefaults(): void {
  if (registered) return
  ChartJS.register(
    BarController,
    BarElement,
    CategoryScale,
    Filler,
    Legend,
    LinearScale,
    LineController,
    LineElement,
    PointElement,
    Tooltip,
    annotationPlugin,
  )

  const tokens = readPaperTokens()
  ChartJS.defaults.color = tokens.ink3
  ChartJS.defaults.borderColor = tokens.line
  ChartJS.defaults.font.family = tokens.fontSans
  ChartJS.defaults.font.size = 11

  // Motion budget — keep below the 150ms "base" token. Honour reduced motion.
  ChartJS.defaults.animation = prefersReducedMotion()
    ? false
    : { duration: 150, easing: 'easeOutQuad' }

  ChartJS.defaults.plugins.tooltip = {
    ...ChartJS.defaults.plugins.tooltip,
    backgroundColor: tokens.ink,
    titleColor: tokens.paper,
    bodyColor: tokens.paper,
    titleFont: { family: tokens.fontSans, weight: 600, size: 12 },
    bodyFont: { family: tokens.fontMono, size: 11 },
    padding: 8,
    cornerRadius: 6,
    displayColors: false,
    boxPadding: 4,
  }

  registered = true
}
